/**
 * GlobalTooltip
 *
 * A lightweight, portal-based tooltip that listens for mouseenter/mouseleave
 * on any element with a [data-tooltip] attribute and renders a fixed-position
 * tooltip at the document root — completely immune to parent overflow:hidden.
 *
 * No per-component changes needed; just mount <GlobalTooltip /> once in App.tsx.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipState {
  text: string;
  x: number;
  y: number;
  visible: boolean;
}

const OFFSET = 10; // px gap between target edge and tooltip
const MIN_MARGIN = 8; // px from viewport edge

export function GlobalTooltip() {
  const [tip, setTip] = useState<TooltipState>({ text: '', x: 0, y: 0, visible: false });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeTarget = useRef<HTMLElement | null>(null);
  const suppressShowUntil = useRef<number>(0);

  useEffect(() => {
    function getTooltipTarget(el: EventTarget | null): HTMLElement | null {
      let node = el as HTMLElement | null;
      while (node && node !== document.body) {
        if (node.dataset?.tooltip) {
          // Suppress generic table-row tooltips; keep explicit tooltips on
          // cells, headers, buttons, and other controls.
          if (node.tagName === 'TR') return null;
          return node;
        }
        node = node.parentElement;
      }
      return null;
    }

    function computePositionForTarget(target: HTMLElement, tipW: number, tipH: number) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const rect = target.getBoundingClientRect();

      // Prefer centered above target; flip below if needed.
      let x = rect.left + (rect.width - tipW) / 2;
      let y = rect.top - tipH - OFFSET;
      if (y < MIN_MARGIN) {
        y = rect.bottom + OFFSET;
      }

      // Clamp to viewport bounds
      x = Math.max(MIN_MARGIN, Math.min(x, vw - tipW - MIN_MARGIN));
      y = Math.max(MIN_MARGIN, Math.min(y, vh - tipH - MIN_MARGIN));

      return { x, y };
    }

    function show(e: MouseEvent) {
      if (Date.now() < suppressShowUntil.current) return;

      const target = getTooltipTarget(e.target);
      if (!target) return;

      const text = target.dataset.tooltip;
      if (!text) return;

      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      activeTarget.current = target;

      // Use a temporary measurement div to get actual tooltip dimensions
      const tmp = document.createElement('div');
      tmp.style.cssText = [
        'position:fixed',
        'visibility:hidden',
        'pointer-events:none',
        'max-width:320px',
        'min-width:max-content',
        'white-space:normal',
        'padding:8px 12px',
        'font-size:12px',
        'line-height:1.35',
        'border-radius:8px',
        'border:2px solid transparent',
        'word-break:break-word',
        'z-index:-1',
      ].join(';');
      tmp.textContent = text;
      document.body.appendChild(tmp);
      const { offsetWidth: tipW, offsetHeight: tipH } = tmp;
      document.body.removeChild(tmp);

      const { x, y } = computePositionForTarget(target, tipW, tipH);
      setTip({ text, x, y, visible: true });
    }

    function hide(e: MouseEvent) {
      const target = getTooltipTarget(e.target);
      if (!target) return;

      // Ignore transitions inside the same tooltip host (e.g., link -> svg path).
      const related = e.relatedTarget as Node | null;
      if (related && target.contains(related)) return;

      activeTarget.current = null;
      hideTimer.current = setTimeout(() => setTip(prev => ({ ...prev, visible: false })), 80);
    }

    function forceHide() {
      activeTarget.current = null;
      suppressShowUntil.current = Date.now() + 200;
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      setTip(prev => (prev.visible || prev.text ? { text: '', x: 0, y: 0, visible: false } : prev));
    }

    function onVisibilityChange() {
      if (document.hidden) {
        forceHide();
      }
    }

    document.addEventListener('mouseenter', show, true);
    document.addEventListener('mouseleave', hide, true);
    document.addEventListener('pointerdown', forceHide, true);
    document.addEventListener('click', forceHide, true);
    document.addEventListener('keydown', forceHide, true);
    document.addEventListener('focusin', forceHide, true);
    document.addEventListener('scroll', forceHide, true);
    document.addEventListener('visibilitychange', onVisibilityChange, true);
    window.addEventListener('blur', forceHide);

    return () => {
      document.removeEventListener('mouseenter', show, true);
      document.removeEventListener('mouseleave', hide, true);
      document.removeEventListener('pointerdown', forceHide, true);
      document.removeEventListener('click', forceHide, true);
      document.removeEventListener('keydown', forceHide, true);
      document.removeEventListener('focusin', forceHide, true);
      document.removeEventListener('scroll', forceHide, true);
      document.removeEventListener('visibilitychange', onVisibilityChange, true);
      window.removeEventListener('blur', forceHide);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  if (!tip.text) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        left: tip.x,
        top: tip.y,
        zIndex: 9999,
        maxWidth: 320,
        pointerEvents: 'none',
        transition: 'opacity 120ms ease',
        opacity: tip.visible ? 1 : 0,
      }}
      className="
        bg-stone-800 dark:bg-nuguard-card
        text-white dark:text-slate-200
        border border-stone-600 dark:border-nuguard-edge
        rounded-lg px-3 py-2
        text-xs leading-snug
        shadow-xl
        break-words whitespace-pre-line text-left
      "
    >
      {tip.text}
    </div>,
    document.body,
  );
}
